import React, { useState } from 'react';
import { Entity } from '@backstage/catalog-model';
import {
  WarningPanel,
  CodeSnippet,
} from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import {
  catalogApiRef,
  CATALOG_FILTER_EXISTS,
} from '@backstage/plugin-catalog-react';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import { useAsyncFn, useDebounce } from 'react-use';
import Grid2 from '@mui/material/Grid2';
import { Card, CardActionArea, CardMedia, CardContent } from '@mui/material';

const ICON_ANNOTATION = 'backstage/logo-url';
const FEATURED_ANNOTATION = 'backstage/featured';

const CatalogFeaturedCards = () => {
  const catalogApi = useApi(catalogApiRef);
  const [entities, setEntities] = useState<Entity[]>([]);
  const [activeFilters] = useState<string[]>([]);
  const [{ loading, error }, refresh] = useAsyncFn(
    async () => {
      const response = await catalogApi.getEntities({
        filter: {
          [`metadata.annotations.${ICON_ANNOTATION}`]: CATALOG_FILTER_EXISTS,
          [`metadata.annotations.${FEATURED_ANNOTATION}`]: 'true',
        },
      });
      setEntities(response.items);
    },
    [catalogApi],
    { loading: true },
  );
  useDebounce(refresh, 10);

  if (error) {
    return (
      <WarningPanel severity="error" title="Could not fetch catalog entities.">
        <CodeSnippet language="text" text={error.toString()} />
      </WarningPanel>
    );
  }

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <Grid2 container direction="column" spacing={1}>
      {entities
        .filter(
          e =>
            activeFilters.length === 0 ||
            activeFilters.includes(e?.spec?.domain as string),
        )
        .map(e => (
          <Grid2 key={e.metadata.name} size={12}>
            <Card sx={{ width: '100%' }}>
              <CardActionArea
                href={`/catalog/default/${e.kind}/${e.metadata.name}`}
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                }}
              >
                <CardMedia
                  sx={{
                    height: 56,
                    width: 56,
                    objectFit: 'contain',
                    margin: 1.5,
                    flexShrink: 0,
                  }}
                  image={e.metadata.annotations?.[ICON_ANNOTATION]}
                  title={e.metadata.name}
                  component="img"
                />
                <CardContent sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography
                    variant="h6"
                    sx={{ color: 'text.primary', fontWeight: 600 }}
                  >
                    {e.metadata.title || e.metadata.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: 'text.secondary' }}
                  >
                    {e.metadata.description}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid2>
        ))}
    </Grid2>
  );
};

export default CatalogFeaturedCards;